import BaseUrlIcon from '@/assets/svg/base-url.svg?react';
import { ApiMethodIcon } from '@/components/api-method-icon/ApiMethodIcon';
import { ApiMethodIconSize } from '@/components/api-method-icon/type/ApiMethodIconSize';
import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { useCalcNavbarHeight } from '@/hooks/useCalcNavbarHeight';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { useOperationConfigModelStore } from '@/models/operation-config/operationConfig';
import type { IApiTypeServerType } from '@/shared/constant/ApiType';
import { ApiType } from '@/shared/constant/ApiType';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { ScreenSize } from '@/shared/constant/ScreenSize';
import { ModelType } from '@tarslib/types/lib/common';
import { EnvironmentMockType } from '@tarslib/types/lib/environment';
import type { RequestMethodType } from '@tarslib/types/lib/httpApi';
import { Button } from 'aui';
import { FoxIconPlay, FoxIconPlay02 } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import { ApiRequestUrl } from '../api-request-url/ApiRequestUrl';
import AsyncEnvironmentModal from '../environment-modal/AsyncEnvironmentModal';
import { useVariablesFromUrl } from '../environment-modal/hooks/useVariablesFromUrl';
import { EnvironmentSelect } from './components/environment-select/EnvironmentSelect';

export type EnvironmentUrlProps = {
  value:
    | {
        type: ModelType.HttpApi;
        method: RequestMethodType;
        path: string;
        apiType: IApiTypeServerType;
        webhookName?: string;
      }
    | {
        type: ModelType.CUSTOM_ENDPOINT;
        protocol: string;
        path: string;
      }
    | {
        type: ModelType.WebSocketApi;
        path: string;
      }
    | {
        type: ModelType.SOCKET_IO_API;
        path: string;
      };

  handleRunInApp?: () => void;
  handleTryItOut?: () => void;
  hasRightSamplePadding?: boolean;

  className?: string;
};

export function EnvironmentUrl(props: EnvironmentUrlProps) {
  const { value, handleRunInApp, handleTryItOut, className, hasRightSamplePadding } = props;

  const navbarHeight = useCalcNavbarHeight();
  const isMobileSize = useAppearanceConfigModelStore((x) => x.screenSize === ScreenSize.Mobile);
  const isOpenDebugPanel = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);

  const hideTryItOutButtonConfig = useDocsDataModelStore(
    (x) => x.docsBaseConfig.hideTryItOutButton,
  );
  const hideRunInAppButtonConfig = useDocsDataModelStore(
    (x) => x.docsBaseConfig.hideRunInAppButton,
  );
  const hideTryItOutButton = React.useMemo(() => {
    return value.type !== ModelType.HttpApi || hideTryItOutButtonConfig || isOpenDebugPanel;
  }, [value.type, hideTryItOutButtonConfig, isOpenDebugPanel]);

  const hideRunInAppButton = React.useMemo(() => {
    return value.type !== ModelType.HttpApi || hideRunInAppButtonConfig;
  }, [value.type, hideRunInAppButtonConfig]);
  const showBaseURL = useDocsDataModelStore((x) => x.docsBaseConfig.isShowPrefixUrl);
  const environmentList = useDocsDataModelStore((x) => x.environments.environmentList);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const APP_NAME = useEnvConfigModelStore((x) => x.APP_NAME);
  const environmentModalVisible = useOperationConfigModelStore((x) => x.environmentModalVisible);
  const setEnvironmentModalVisible = useOperationConfigModelStore(
    (x) => x.setEnvironmentModalVisible,
  );
  const selectedEnvironment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);
  const environmentModalFocusKey = useOperationConfigModelStore((x) => x.environmentModalFocusKey);

  const isWebSocketInMock = React.useMemo(() => {
    return (
      value.type === ModelType.WebSocketApi &&
      selectedEnvironment?.type &&
      EnvironmentMockType.includes(selectedEnvironment.type)
    );
  }, [value, selectedEnvironment]);

  const hasEnvironmentSelect = React.useMemo(() => {
    const modelTypeSelect = [ModelType.HttpApi, ModelType.WebSocketApi];

    return modelTypeSelect.includes(value.type) && environmentList.length > 1 && !isWebSocketInMock;
  }, [value, environmentList.length, isWebSocketInMock]);

  // load client environment variables and merge with environment variables in URL
  useVariablesFromUrl();

  const isWebhook = React.useMemo(() => {
    return value.type === ModelType.HttpApi && value.apiType === ApiType.Webhook;
  }, [value]);

  return (
    <>
      {!isWebhook && hasEnvironmentSelect && (
        <EnvironmentSelect
          className={clsx('flex items-center py-1', {
            ['@[1000px]/http-api-content:hidden']: !hasRightSamplePadding,
            ['@[1440px]/http-api-content:hidden']: hasRightSamplePadding,
          })}
          selectClassName="max-w-full text-sm font-400 text-input"
          size="small"
          variant="text"
        />
      )}

      <ContainerQuery
        containerSizes={[500, 998]}
        className={clsx(
          '@container/environment-url',
          'z-10 flex items-stretch rounded-xl border border-border-color-base bg-input shadow-sm',
          {
            ['sticky']: !isMobileSize,
          },
          className,
        )}
        style={{
          top: isMobileSize ? undefined : navbarHeight + 8,
        }}
      >
        {!isWebhook && hasEnvironmentSelect && (
          <EnvironmentSelect
            className={clsx(
              'hidden items-center border-r border-border-color-base @[998px]/environment-url:flex',
            )}
            selectClassName="max-w-[200px] text-lg font-base text-secondary"
            bordered={false}
            size="large"
          />
        )}
        <div className={clsx('flex w-0 flex-1 items-stretch p-1')}>
          {value.type !== ModelType.WebSocketApi && value.type !== ModelType.SOCKET_IO_API && (
            <div className="m-1 flex items-center">
              <ApiMethodIcon
                value={value}
                className="flex h-full max-h-[90px] !rounded"
                size={ApiMethodIconSize.Large}
                apiLabelSize={ApiMethodIconSize.Large}
              />
            </div>
          )}
          <div className="flex min-h-[32px] w-0 flex-1 items-center break-all font-400 leading-5 text-color">
            <ApiRequestUrl
              showBaseURL={showBaseURL}
              showPathOnly={value.type === ModelType.SOCKET_IO_API || isWebSocketInMock}
              forceHideBaseUrl={isWebhook}
              path={
                value.type === ModelType.HttpApi && isWebhook ? value.webhookName || '' : value.path
              }
              copyable
              showIconOnly={!showBaseURL}
              selectInline
              selectProps={{
                optionLabelIconOnly: !showBaseURL,
                optionLabelIcon: <BaseUrlIcon className="text-[15px] text-secondary" />,
              }}
              whitespace="normal"
            />
          </div>

          {(!hideTryItOutButton || !hideRunInAppButton) && (
            <div className="ml-3 flex items-center gap-1">
              {!hideTryItOutButton && (
                <Button
                  type="primary"
                  size="middle"
                  className={clsx(
                    'h-full max-h-14 animate-none transition-none @[998px]/environment-url:h-auto',
                  )}
                  icon={<FoxIconPlay size={14} />}
                  onClick={handleTryItOut}
                  aria-label={clientI18n('tryItOut')}
                >
                  <span className="hidden @[500px]/environment-url:inline-block">
                    {clientI18n('tryItOut')}
                  </span>
                </Button>
              )}

              {!hideRunInAppButton && (
                <Button
                  type="default"
                  size="middle"
                  className={clsx(
                    'hidden animate-none transition-none @[998px]/environment-url:flex',
                  )}
                  icon={<FoxIconPlay02 size={14} />}
                  onClick={handleRunInApp}
                >
                  {clientI18n('runInApp', {
                    appName: APP_NAME,
                  })}
                </Button>
              )}
            </div>
          )}
        </div>
      </ContainerQuery>

      {!hideRunInAppButton && (
        <Button
          type="soft-secondary"
          size="small"
          className={clsx('mr-2 mt-2 animate-none transition-none', {
            ['@[1000px]/http-api-content:hidden']: !hasRightSamplePadding,
            ['@[1440px]/http-api-content:hidden']: hasRightSamplePadding,
          })}
          icon={<FoxIconPlay02 size={12} />}
          onClick={handleRunInApp}
        >
          {clientI18n('runInApp', {
            appName: APP_NAME,
          })}
        </Button>
      )}

      {environmentModalVisible && (
        <AsyncEnvironmentModal
          visible={environmentModalVisible}
          onClose={() => setEnvironmentModalVisible(false)}
          autoFocusKey={environmentModalFocusKey}
        />
      )}
    </>
  );
}
